import { UnapprovedTag } from "./UnapprovedSupplierDialog";
import { itemMatchesSupplier } from "../../lib/suppliers";
import { toast as sonnerToast } from "sonner";

const warnIfSame = (li) => {
  if (!itemMatchesSupplier(li)) return;
  sonnerToast.warning("Item name matches the supplier", {
    id: `item-supplier-${li.id}`,
    description: `"${li.product_name}" is the supplier name. Please enter what the item actually is (e.g. "Torsion spring 50mm").`,
    duration: 8000,
  });
};
import React from "react";
import { Plus, Trash2, GripVertical, FileUp, Loader2, FileText, Image as ImageIcon } from "lucide-react";
import { api, gbp, openFile } from "../../lib/apiClient";
import { Section } from "./layout";
import { useRowDrag } from "./useRowDrag";
import { MarkupCell } from "./MarkupCell";
import { numVal, parseNum, tmpId, CATEGORIES } from "./utils";

/**
 * MaterialsTable — the Parts & Materials section of the Quote Builder.
 *
 * Owns its own row-level add/update/remove logic plus the PDF upload UX
 * (which delegates the actual extract call to the parent via `onPdfFile`).
 */
export function MaterialsTable({
  items,
  onChange,
  markupOptions = [],
  userInitials = "",
  defaultMarkup = 35,
  isAdmin = false,
  minMarkup = null,
  onPdfFile,
  onImageFile,
  onSupplierBlur,
  quoteId,
  supplierChecksOn,
  uploading = false,
  uploadMsg = "",
  uploadError = "",
  storageKey,
}) {
  const drag = useRowDrag(items || [], onChange, "materials");
  const pdfInputRef = React.useRef(null);
  const imageInputRef = React.useRef(null);

  const addLine = (prefill = {}) => onChange([
    ...(items || []),
    {
      id: tmpId(),
      product_name: "",
      supplier_description: "",
      category: "Parts",
      quantity: 1,
      supplier_cost: 0,
      markup_percent: defaultMarkup,
      ...prefill,
    },
  ]);
  const updateLine = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeLine = (idx) => onChange(items.filter((_, i) => i !== idx));

  const onPdfSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    if (onPdfFile) await onPdfFile(f);
  };

  const onImageSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    if (onImageFile) await onImageFile(f);
  };

  return (
    <Section
      title="Parts & Materials"
      subtitle={uploadError || uploadMsg || "Add manually or drop a supplier PDF to auto-fill"}
      collapsible
      storageKey={storageKey}
      action={
        <div className="flex items-center gap-2">
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={onPdfSelected}
            data-testid="pdf-upload-input"
          />
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={onImageSelected}
            data-testid="image-upload-input"
          />
          <button
            onClick={() => pdfInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-supplier-pdf-button"
            className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 px-3 h-8 border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
            {uploading ? "Reading…" : "Upload PDF"}
          </button>
          <button
            onClick={() => imageInputRef.current?.click()}
            disabled={uploading}
            data-testid="attach-image-button"
            className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 px-3 h-8 border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white disabled:opacity-60"
            title="Snap a supplier quote — Claude reads the cost + supplier name"
          >
            <ImageIcon size={14} /> Attach Image
          </button>
          <button
            onClick={() => addLine()}
            className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 hover:text-slate-900 text-slate-600"
            data-testid="add-line-button"
          >
            <Plus size={14} /> Add line
          </button>
        </div>
      }
    >
      <div>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <table className="w-full text-sm table-fixed min-w-[640px]">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th style={{ width: 28 }} className="px-1 py-2"></th>
              {[
                { label: "Supplier / Item", w: "18%" },
                { label: "Description", w: "17%" },
                { label: "Cat.", w: "9%" },
                { label: "Qty", w: "6%", right: true },
                { label: "Cost ex VAT", w: "12%", right: true },
                { label: "Markup %", w: "10%", right: true },
                { label: "Markup £", w: "11%", right: true },
                { label: "Line Total", w: "13%", right: true },
                { label: "", w: "36px" },
              ].map((h) => (
                <th
                  key={h.label || "actions"}
                  style={{ width: h.w }}
                  className={`px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600 ${h.right ? "text-right" : "text-left"}`}
                >{h.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(items || []).length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400 text-sm">No line items yet.</td></tr>
            )}
            {(items || []).map((li, idx) => {
              const qty = Number(li.quantity || 0);
              const cost = Number(li.supplier_cost || 0);
              const mp = Number(li.markup_percent || 0);
              const markupValue = qty * cost * (mp / 100);
              const ptotal = qty * cost * (1 + mp / 100);
              return (
                <tr key={li.id} className="border-b border-slate-200" {...drag.dragProps(idx)}>
                  <td className="px-1 py-1 align-middle text-slate-400 hover:text-slate-700" {...drag.handleProps(idx)}>
                    <GripVertical size={14} />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      value={li.supplier_name || ""}
                      onChange={(e) => updateLine(idx, { supplier_name: e.target.value })}
                      onFocus={(e) => { e.target.dataset.orig = e.target.value; }}
                      onBlur={(e) => { warnIfSame(li); if (e.target.value !== e.target.dataset.orig) onSupplierBlur?.(li, e.target.value); }}
                      className="qb-input h-9"
                      data-testid={`line-supplier-${idx}`}
                      list={supplierChecksOn ? "supplier-options-materials" : undefined}
                      autoComplete="off"
                      placeholder="Supplier (e.g. Screwfix)"
                    />
                    <input
                      value={li.product_name}
                      onChange={(e) => updateLine(idx, { product_name: e.target.value })}
                      onBlur={() => warnIfSame(li)}
                      className="qb-input h-8 mt-1 text-xs"
                      data-testid={`line-product-${idx}`}
                      placeholder="Item name"
                    />
                    <UnapprovedTag rec={li.unapproved_supplier} testid={`line-unapproved-tag-${idx}`} />
                    {li.source_pdf_id && (
                      <a
                        href="#" onClick={(e) => { e.preventDefault(); openFile(li.source_pdf_id, quoteId); }}
                        className="text-[10px] text-brand-blue hover:text-brand-blue-dark inline-flex items-center gap-1 mt-0.5"
                        data-testid={`line-pdf-link-${idx}`}
                        title={li.source_pdf_name}
                      >
                        <FileText size={10} /> {li.supplier_quote_ref || "View PDF"}
                      </a>
                    )}
                  </td>
                  <td className={`px-1 py-1 ${li.pdf_line_items?.length ? "align-top" : "align-middle"}`}>
                    {li.pdf_line_items && li.pdf_line_items.length > 0 ? (
                      <div data-testid={`line-pdf-summary-${idx}`} className="space-y-1.5 py-1">
                        <div className="flex items-center flex-wrap gap-2 text-xs">
                          {li.supplier_quote_ref && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-brand-blue font-mono text-[11px]">
                              Ref · {li.supplier_quote_ref}
                            </span>
                          )}
                          <span className="text-slate-500">
                            {li.pdf_line_items.length} item{li.pdf_line_items.length === 1 ? "" : "s"} from supplier quote
                          </span>
                        </div>
                        <details className="text-[12px]">
                          <summary className="text-[10px] uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-900">
                            Show items / notes
                          </summary>
                          <ul className="mt-1.5 pl-4 list-disc space-y-0.5 text-slate-600 marker:text-slate-300">
                            {li.pdf_line_items.slice(0, 8).map((pi, i) => (
                              <li key={i}>
                                {pi.description}
                                {pi.quantity ? <span className="text-slate-400"> · {pi.quantity}</span> : null}
                                {pi.line_total != null ? <span className="text-slate-500"> · {gbp(pi.line_total)}</span> : null}
                              </li>
                            ))}
                            {li.pdf_line_items.length > 8 && (
                              <li className="text-slate-400">…and {li.pdf_line_items.length - 8} more</li>
                            )}
                          </ul>
                          <textarea
                            rows={2}
                            value={li.supplier_description}
                            onChange={(e) => updateLine(idx, { supplier_description: e.target.value })}
                            className="qb-input mt-2 text-[12px] leading-snug"
                            placeholder="Notes…"
                          />
                        </details>
                      </div>
                    ) : (
                      <input
                        value={li.supplier_description || ""}
                        onChange={(e) => updateLine(idx, { supplier_description: e.target.value })}
                        className="qb-input h-9"
                        placeholder="Short description"
                      />
                    )}
                  </td>
                  <td className="px-1 py-1">
                    <select
                      value={li.category}
                      onChange={(e) => updateLine(idx, { category: e.target.value })}
                      className="qb-input h-9"
                    >
                      {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" step="0.01"
                      value={numVal(li.quantity)}
                      onChange={(e) => updateLine(idx, { quantity: parseNum(e.target.value) })}
                      className="qb-input h-9 font-mono text-right"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" step="0.01"
                      value={numVal(li.supplier_cost)}
                      onChange={(e) => updateLine(idx, { supplier_cost: parseNum(e.target.value) })}
                      className="qb-input h-9 font-mono text-right"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <MarkupCell
                      value={li.markup_percent}
                      presets={markupOptions}
                      userInitials={userInitials}
                      overrideBy={li.markup_override_by}
                      overrideAt={li.markup_override_at}
                      onChangeWithAudit={(pct, audit) => updateLine(idx, { markup_percent: pct, ...audit })}
                      testId={`line-markup-${idx}`}
                      isAdmin={isAdmin}
                      minPercent={minMarkup}
                    />
                  </td>
                  <td className="px-2 py-1 font-mono text-right whitespace-nowrap text-slate-500" data-testid={`line-markup-value-${idx}`}>{gbp(markupValue)}</td>
                  <td className="px-2 py-1 font-mono text-right whitespace-nowrap font-semibold">{gbp(ptotal)}</td>
                  <td className="px-2 py-1 text-right">
                    <button onClick={() => removeLine(idx)} className="text-slate-400 hover:text-red-600" data-testid={`remove-line-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </Section>
  );
}
